<template>
  <div class="text-left" style="display: inline">
    <slot :trigger-modal="openModal">
      <BaseButton
        v-if="!viewOnly"
        data-test="expense-report-create-new-report-button"
        @click="openModal"
      >
        + {{ $t('Create new') }}
      </BaseButton>
    </slot>
    <Teleport to="body">
      <BaseModal
        v-cloak
        :modal-id="`report-create-${expenseReport.localId || ''}`"
        :header-text="modalTitle"
        :save-state="saveState"
        :is-open="open"
        :disabled="!report.title || (!isHireProvidedByProps && !hasSelectedHire) || viewOnly"
        :remove-confirm-button="viewOnly"
        :cancel-button-text="cancelButtonText"
        :data-test="
          expenseReport.localId
            ? `expense-report-modal-${expenseReport.localId}`
            : `expense-report-modal`
        "
        @save="saveReport"
        @cancel="closeModal"
      >
        <div class="m-t-10">
          <label class="m-t-10 f-500">{{ $t('Title') }} *</label>
          <input
            v-model="report.title"
            :disabled="viewOnly"
            :placeholder="$t('Enter title')"
            name="report-title"
            type="text"
            class="form-control"
            data-test="expense-report-modal-title"
          />

          <label class="m-t-20 f-500">{{ $t('Description') }} *</label>
          <!-- eslint-disable-next-line vue/no-undef-components -- Baseline eslint ignore please fix if working on this-->
          <BaseTextareaAutoresize
            v-model="report.description"
            :disabled="viewOnly"
            :placeholder="$t('Enter description')"
            name="report-description"
            data-test="expense-report-modal-description"
          />
          <template v-if="!isHireProvidedByProps">
            <WdsSelect
              v-model="selectedHire"
              class="m-t-10"
              :disabled="viewOnly"
              :label="$t('Hire')"
              :list="hireOptions"
              :title="$t('Hire')"
              :data-test="`expense-report-modal-job-select${expenseReport.localId ? '-' + expenseReport.localId : ''}`"
              required
            />
            <transition name="fade">
              <div v-if="billOptions.length" class="m-t-10" data-test="payment-request">
                <WdsSelect
                  v-model="selectedBill"
                  :disabled="viewOnly || !hasSelectedHire"
                  :label="$t('Bill')"
                  :list="billOptions"
                  :title="$t('Bill')"
                  :can-deselect="Object.entries(expenseReport).length === 0"
                  :data-test="`expense-report-modal-bill-select${expenseReport.localId ? '-' + expenseReport.localId : ''}`"
                />
              </div>
            </transition>
          </template>
          <transition name="fade">
            <div
              v-if="!viewOnly && selectedHireObject && selectedHireObject.hasFreelancerVat"
              class="alert alert-info m-t-10"
              role="alert"
            >
              {{ $t('For this job, please make sure to register your expenses excluding :vatTerminology', { vatTerminology }) }}
            </div>
          </transition>
        </div>
      </BaseModal>
    </Teleport>
  </div>
</template>

<script>
import moment from 'moment'
import gql from 'graphql-tag'
import { WdsSelect } from '@worksome/wds-components'
import { EXPENSE_REPORT_MUTATIONS } from '@/state/mutation-types'
import FormatDateMixin from '@/mixins/MethodFormatDateMixin'
import BaseModal from '@/components/BaseModal.vue'
import BaseButton from '@/components/BaseButton.vue'
import BaseTextareaAutoresize from '@/components/BaseTextareaAutoresize.vue'

export default {
  components: { BaseTextareaAutoresize, WdsSelect, BaseButton, BaseModal },
  mixins: [FormatDateMixin],
  props: {
    defaultBill: { type: [Number, String], default: '' },
    expenseReport: { type: Object, default: () => ({}) },
    hireId: { type: [Number, String], default: '' },
    billId: { type: [Number, String], default: '' },
    freelancerId: { type: String, default: '' },
    hires: { type: Array, default: () => [] },
    bills: { type: Array, default: () => [] },
    viewOnly: { type: Boolean, default: false },
  },
  data() {
    return {
      open: false,
      saveState: 'fresh',
      selectedHire: {},
      selectedBill: {},
      report: {
        title: null,
        description: null,
        hire_id: null,
        bill_id: null,
      },
    }
  },
  computed: {
    cancelButtonText() {
      return this.viewOnly ? $t('Close') : $t('Cancel')
    },
    isHireProvidedByProps() {
      return !!this.hireId
    },
    hasSelectedHire() {
      return Boolean(this.selectedHireId)
    },
    selectedHireId() {
      return this.report.hire_id
    },
    availableBillsForSelectedHire() {
      return this.bills.filter(
        (bill) => parseInt(bill.hire.localId, 10) === parseInt(this.selectedHireId, 10),
      )
    },
    selectedHireObject() {
      return this.hires.find(
        (hire) => parseInt(hire.localId, 10) === parseInt(this.selectedHireId, 10),
      )
    },
    modalTitle() {
      if (this.viewOnly) {
        return $t('Expense report')
      }
      return $t(
        Object.entries(this.expenseReport).length > 0
          ? 'Update expense report'
          : 'Create expense report',
      )
    },
    vatTerminology() {
      return vatTerminology
    },
    billOptions() {
      const bills = this.availableBillsForSelectedHire.map((bill) => ({
        value: bill.localId,
        label: `#${bill.localId}`,
        subLabel: `${bill.yjob.name.substring(0, 100)} ${bill.yjob.name.length > 100 ? '...' : ''}`,
      }))

      if (Object.entries(this.expenseReport).length === 0) {
        bills.unshift({
          value: null,
          label: $t('None'),
        })
      }

      return bills
    },
    hireOptions() {
      return this.hires.map((hire) => ({
        value: hire.localId,
        label: hire.yjob.name,
        subLabel: $t(':company, :startDate - :endDate', {
          company: hire.company.name,
          startDate: hire.dateStart
            ? moment(hire.dateStart).format('MMM D YYYY')
            : $t('No start date'),
          endDate: hire.dateEnd ? moment(hire.dateEnd).format('MMM D YYYY') : $t('No end date'),
        }),
      }))
    },
  },
  watch: {
    selectedHire(selected) {
      this.report.hire_id = selected?.value

      const initialBill = this.billOptions.find(
        (bill) => parseInt(bill.value, 10) === parseInt(this.defaultBill, 10),
      )

      if (initialBill) {
        this.report.bill_id = initialBill.value
      }
    },
    selectedBill(selected) {
      this.report.bill_id = selected?.value
    },
  },
  methods: {
    $t(text) {
      return $t(text)
    },
    setHireFromDefaultBill() {
      const hireId = this.bills.find(
        (bill) => parseInt(bill.localId, 10) === parseInt(this.defaultBill, 10),
      )?.hire?.localId

      if (hireId) {
        this.selectedHire = this.hireOptions.find(
          (hire) => parseInt(hire.value, 10) === parseInt(hireId, 10),
        )
      } else {
        this.selectedHire = null
      }
    },
    openModal() {
      this.open = true
      this.setHireFromDefaultBill()
      this.setInitialReportState()
    },
    saveReport() {
      if (Object.entries(this.expenseReport).length > 0) {
        this.updateExpenseReport()
      } else {
        this.createExpenseReport()
      }
    },
    setInitialReportState() {
      if (Object.entries(this.expenseReport).length > 0) {
        this.report.title = this.expenseReport.title
        this.report.description = this.expenseReport.description
        this.report.hire_id = this.expenseReport.hire_id
        this.report.bill_id = this.expenseReport.bill_id

        this.selectedHire = this.expenseReport.hire_id
          ? this.hireOptions.find(
              (hire) => parseInt(hire.value, 10) === parseInt(this.expenseReport.hire_id, 10),
            )
          : null

        this.selectedBill = this.expenseReport.bill_id
          ? this.billOptions.find(
              (bill) => parseInt(bill.value, 10) === parseInt(this.expenseReport.bill_id, 10),
            )
          : null
      } else {
        this.setEmptyReportState()
      }
    },
    closeModal() {
      this.open = false
      this.setEmptyReportState()
    },
    setEmptyReportState() {
      this.selectedHire = null
      this.selectedBill = null
      this.report.title = null
      this.report.description = null
      this.report.hire_id = null
      this.report.bill_id = null
    },
    createExpenseReport() {
      this.saveState = 'saving'
      this.$apollo
        .mutate({
          fetchPolicy: 'no-cache',
          mutation: gql`
            mutation (
              $title: String!
              $description: String
              $freelancerId: ID!
              $billId: ID
              $hireId: ID
            ) {
              createExpenseReport(
                input: {
                  title: $title
                  description: $description
                  freelancer: { connect: $freelancerId }
                  bill: { connect: $billId }
                  hire: { connect: $hireId }
                }
              ) {
                globalId
                localId
                title
                description
                isDraft
                bill_id
                hire_id
                createdAt
                bill {
                  globalId
                  localId
                  poNumber
                  currency
                  isIssued
                  isPayed
                  isExpresspayed
                }
                hire {
                  globalId
                  localId
                  hasFreelancerVat
                  currency
                  yjob {
                    globalId
                    localId
                    name
                    currency
                  }
                  company {
                    globalId
                  }
                  customFieldProviderCompanies {
                    globalId
                  }
                }
                expenses {
                  globalId
                  localId
                  vendor
                  description
                  amount
                  currency
                  expenseAt
                  customFieldValues {
                    id: globalId
                    content
                    displayValue
                    customField {
                      id: globalId
                      title
                      fieldType
                    }
                    customFieldOption {
                      id: globalId
                      value
                    }
                  }
                  files {
                    globalId
                    localId
                    name
                    icon {
                      icon
                      color
                      thumbnail
                    }
                  }
                }
              }
            }
          `,
          variables: {
            title: this.report.title,
            description: this.report.description,
            freelancerId: this.freelancerId || this.account.globalId,
            billId: this.billId || this.report.bill_id || null,
            hireId: this.hireId || this.report.hire_id,
          },
        })
        .then((data) => {
          this.saveState = 'saved'
          const savedExpenseReport = data?.data?.createExpenseReport
          if (savedExpenseReport) {
            this.$store.commit(EXPENSE_REPORT_MUTATIONS.ADD_REPORT, savedExpenseReport)
            this.$emit('report-created', savedExpenseReport)
          }
          setTimeout(() => {
            this.closeModal()
          }, 500)
        })
        .catch(() => {
          this.saveState = 'failed'
        })
        .finally(() => {
          setTimeout(() => {
            this.saveState = 'fresh'
          }, 1000)
        })
    },
    updateExpenseReport() {
      this.saveState = 'saving'
      this.$apollo
        .mutate({
          fetchPolicy: 'no-cache',
          mutation: gql`
            mutation (
              $reportId: Int!
              $title: String!
              $description: String
              $billId: ID
              $hireId: ID
            ) {
              updateExpenseReport(
                input: {
                  id: $reportId
                  title: $title
                  description: $description
                  hire: { connect: $hireId }
                  bill: { connect: $billId }
                }
              ) {
                globalId
                localId
                title
                description
                isDraft
                bill_id
                hire_id
                createdAt
                bill {
                  globalId
                  localId
                  poNumber
                  currency
                  isIssued
                  isPayed
                  isExpresspayed
                }
                hire {
                  globalId
                  localId
                  hasFreelancerVat
                  currency
                  yjob {
                    globalId
                    localId
                    name
                    currency
                  }
                  company {
                    globalId
                  }
                  customFieldProviderCompanies {
                    globalId
                  }
                }
                expenses {
                  globalId
                  localId
                  vendor
                  description
                  amount
                  currency
                  expenseAt
                  customFieldValues {
                    id: globalId
                    content
                    displayValue
                    customField {
                      id: globalId
                      title
                      fieldType
                    }
                    customFieldOption {
                      id: globalId
                      value
                    }
                  }
                  files {
                    globalId
                    localId
                    name
                    icon {
                      icon
                      color
                      thumbnail
                    }
                  }
                }
              }
            }
          `,
          variables: {
            reportId: parseInt(this.expenseReport.localId, 10),
            title: this.report.title,
            description: this.report.description,
            billId: this.report.bill_id,
            hireId: this.report.hire_id,
          },
        })
        .then((data) => {
          this.saveState = 'saved'
          const updatedExpenseReport = data?.data?.updateExpenseReport
          if (updatedExpenseReport) {
            this.$store.commit(EXPENSE_REPORT_MUTATIONS.UPDATE_REPORT, {
              reportId: this.expenseReport.localId,
              updatedReport: updatedExpenseReport,
            })
            this.$emit('report-updated', updatedExpenseReport)
          }
          setTimeout(() => {
            this.closeModal()
          }, 500)
        })
        .catch(() => {
          this.saveState = 'failed'
        })
        .finally(() => {
          setTimeout(() => {
            this.saveState = 'fresh'
          }, 1000)
        })
    },
  },
}
</script>

<style>
.fade-enter-active,
.fade-leave-active {
  transition: var(--default-fade-transition);
}
.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}
</style>
